"use client";

import { useEffect, useRef } from "react";

// "Facet Fold" from the Randstack pattern library: a low-poly triangulated
// mesh that folds like paper — always drifting on ambient waves, folding
// further around the cursor. Drawn on a transparent canvas so the section's
// background shows through.

interface MeshPoint {
  bx: number;
  by: number;
  x: number;
  y: number;
  z: number;
  lift: number;
  mouseLift: number;
}

function hexToRgb(hex: string): string {
  const value = hex.replace("#", "");
  const n = parseInt(
    value.length === 3 ? value.split("").map((ch) => ch + ch).join("") : value,
    16
  );
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

export function FacetFold({
  color = "#6ea3e0",
  cols = 16,
  speed = 1,
  hover = 1,
  // Scales the fill/stroke opacity ramps (1 = the library's full-strength look).
  opacity = 1,
  className
}: {
  color?: string;
  cols?: number;
  speed?: number;
  hover?: number;
  opacity?: number;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rgb = hexToRgb(color);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0;
    let H = 0;
    let t = 0;
    let rows = 0;
    let cellW = 0;
    let pts: MeshPoint[][] = [];
    let rafId = 0;
    let running = false;
    let visible = false;
    const mouse = { x: -9999, y: -9999, active: false };

    function build() {
      rows = Math.max(4, Math.round(cols * (H / W || 1)));
      cellW = W / (cols - 1);
      const cellH = H / (rows - 1);
      pts = [];
      for (let r = 0; r < rows; r++) {
        const row: MeshPoint[] = [];
        for (let c = 0; c < cols; c++) {
          row.push({ bx: c * cellW, by: r * cellH, x: c * cellW, y: r * cellH, z: 0, lift: 0, mouseLift: 0 });
        }
        pts.push(row);
      }
    }

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = wrap!.clientWidth;
      H = wrap!.clientHeight;
      canvas!.width = W * dpr;
      canvas!.height = H * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    }

    function tri(a: MeshPoint, b: MeshPoint, c: MeshPoint) {
      const avgZ = (a.z + b.z + c.z) / 3;
      ctx!.beginPath();
      ctx!.moveTo(a.x, a.y);
      ctx!.lineTo(b.x, b.y);
      ctx!.lineTo(c.x, c.y);
      ctx!.closePath();
      ctx!.fillStyle = `rgba(${rgb},${((0.03 + avgZ * 0.16) * opacity).toFixed(3)})`;
      ctx!.fill();
      ctx!.strokeStyle = `rgba(${rgb},${(Math.min(0.9, 0.14 + avgZ * 0.7) * opacity).toFixed(3)})`;
      ctx!.lineWidth = 0.75 + avgZ * 1.1;
      ctx!.stroke();
    }

    function render() {
      ctx!.clearRect(0, 0, W, H);
      const influence = Math.min(W, H) * 0.3;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const pt = pts[r][c];
          const waveA = Math.sin(t * 0.35 * speed + pt.bx * 0.008 + pt.by * 0.005);
          const waveB = Math.sin(t * 0.22 * speed - pt.bx * 0.004 + pt.by * 0.011 + 1.7);
          const ambient = 0.16 + 0.14 * waveA + 0.09 * waveB;
          let mTarget = 0;
          if (mouse.active && !reduceMotion) {
            const dx = pt.bx - mouse.x;
            const dy = pt.by - mouse.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < influence) mTarget = Math.pow(1 - dist / influence, 2.2) * 0.85 * hover;
          }
          const rate = mTarget > pt.mouseLift ? 0.22 : 0.07;
          pt.mouseLift += (mTarget - pt.mouseLift) * rate;
          pt.lift = Math.max(0, Math.min(1.4, ambient + pt.mouseLift));
          pt.x = pt.bx;
          pt.y = pt.by - pt.lift * cellW * 0.55;
          pt.z = pt.lift;
        }
      }
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
          const p00 = pts[r][c];
          const p10 = pts[r][c + 1];
          const p01 = pts[r + 1][c];
          const p11 = pts[r + 1][c + 1];
          if ((r + c) % 2 === 0) {
            tri(p00, p10, p11);
            tri(p00, p11, p01);
          } else {
            tri(p00, p10, p01);
            tri(p10, p11, p01);
          }
        }
      }
    }

    function loop() {
      t += 0.016;
      render();
      rafId = requestAnimationFrame(loop);
    }

    function start() {
      if (running) return;
      running = true;
      rafId = requestAnimationFrame(loop);
    }

    function stop() {
      if (!running) return;
      running = false;
      cancelAnimationFrame(rafId);
    }

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas!.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    };
    const onMouseLeave = () => {
      mouse.active = false;
    };

    const resizeObserver = new ResizeObserver(() => {
      resize();
      render();
    });
    resizeObserver.observe(wrap);

    // Only animate while on screen; with motion reduced, one static frame.
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (reduceMotion) {
        if (visible) render();
      } else if (visible) {
        start();
      } else {
        stop();
      }
    });
    intersectionObserver.observe(wrap);

    resize();
    render();
    window.addEventListener("mousemove", onMouseMove);
    document.documentElement.addEventListener("mouseleave", onMouseLeave);

    return () => {
      stop();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener("mousemove", onMouseMove);
      document.documentElement.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [color, cols, speed, hover, opacity]);

  return (
    <div ref={wrapRef} aria-hidden="true" className={className}>
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
